import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import { getAllEvents } from 'api/canvms';
import type { VMSEvent, EventCounts, CrossSiteEventFilters } from '../types/canvision.types';

export function useCrossSiteEvents(filters?: CrossSiteEventFilters) {
  const dispatch = useDispatch();
  const [events, setEvents] = useState<VMSEvent[]>([]);
  const [counts, setCounts] = useState<EventCounts>({ new: 0, acknowledged: 0, dismissed: 0, promoted: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchEvents = useCallback(async (isRefresh = false) => {
    try {
      if (!isRefresh && !initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);
      const response = await getAllEvents(filters);
      setEvents(response.data?.events || []);
      setCounts(response.data?.counts || { new: 0, acknowledged: 0, dismissed: 0, promoted: 0 });
      initialLoadDone.current = true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch events';
      console.error('[CANVMS/fetchCrossSiteEvents] Error fetching cross-site events:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [filters?.site_id, filters?.camera_id, filters?.event_type, filters?.status, filters?.start, filters?.end, filters?.limit, dispatch]);

  useEffect(() => {
    initialLoadDone.current = false;
    fetchEvents(false);
  }, [fetchEvents]);

  const refetch = useCallback(() => fetchEvents(true), [fetchEvents]);

  return { events, counts, loading, error, refetch };
}
